import React from 'react';
import dynamic from 'next/dynamic';
import Link from 'components/link';
import SearchIcon from 'components/icon/search-icon';
import { useFeaturesContext } from 'contexts';
import { useIsMobile, useIsIpadMini } from 'hooks/use-size-class';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import { SEARCH_PAGE_ROUTE_NAME } from 'types/search_page_types';
import { RouteName } from 'components/dynamic-page/route-matchers';

// Dynamically import AdvancedSearch component to reduce initial bundle size
const AdvancedSearch = dynamic(() => import('components/search-bar/advanced-search'), {
  loading: () => <></>,
  ssr: false,
});

interface SearchBoxProps {
  routeName?: RouteName;
  searchUrl?: string;
  searchButtonText?: string;
  searchButtonClassName?: string;
  searchIconClassName?: string;
  headerIconClassName?: string;
  panelClassName?: string;
  searchIconTestId?: string;
  mapHeaderTestId?: string;
}

/**
 * SearchBox Component
 * Renders a search interface that adapts between mobile and desktop views
 * Provides both a search icon for mobile and a map link for desktop
 */
const SearchBox = ({ 
  routeName,
  searchUrl = '/search',
  searchButtonText = 'Map',
  searchButtonClassName = '',
  searchIconClassName = '',
  headerIconClassName = '',
  panelClassName = '',
  searchIconTestId,
  mapHeaderTestId,
}: SearchBoxProps) => {
  // Hooks for managing state and responsive behavior
  const { isSearchPanelOpen, setIsSearchPanelOpen } = useFeaturesContext();
  const isMobile = useIsMobile();
  const isIpadMini = useIsIpadMini();
  
  // Determine display mode based on device and route
  const isSearchPage = routeName === SEARCH_PAGE_ROUTE_NAME;
  const shouldShowMobileView = isMobile && !isIpadMini;
  const shouldShowDesktopView = !isMobile || isIpadMini;

  // Toggle search panel visibility
  const toggleSearch = () => {
    setIsSearchPanelOpen(!isSearchPanelOpen);
  };

  // Handle clicks outside the search panel to close it
  const searchSectionOutsideClickHandler = useOutsideClickHandler<HTMLDivElement>(() => {
    if (isSearchPanelOpen) setIsSearchPanelOpen(false);
  });

  return (
    <>
      {/* Mobile Search Icon - Only shown on search page */}
      {isSearchPage && shouldShowMobileView && (
        <span ref={searchSectionOutsideClickHandler}>
          <SearchIcon 
            className={headerIconClassName} 
            onClick={toggleSearch} 
            testId={searchIconTestId} 
          />
          {isSearchPanelOpen && (
            <div className={panelClassName}>
              <AdvancedSearch hideSearchPanel={() => setIsSearchPanelOpen(false)} />
            </div>
          )}
        </span>
      )}
      
      {/* Desktop Map Link - Shown when not on search page or when on desktop */}
      {(!isSearchPage || shouldShowDesktopView) && (
        <Link 
          href={searchUrl} 
          testId={mapHeaderTestId} 
          className={searchButtonClassName}
        >
          <SearchIcon className={searchIconClassName} />
          {searchButtonText}
        </Link>
      )}
    </>
  );
};

export default SearchBox; 